import { HttpErrorResponse } from '@angular/common/http';
import { type MessageKey } from '../i18n/en';
import { t } from '../i18n/t';

/**
 * One error shape for everything the API can refuse with. Built from the RFC 9457 problem
 * body the API returns (`code`, `correlationId`, `errors[]`), so callers branch on `code`
 * and never on message text.
 */
export interface ApiErrorInit {
  readonly status: number;
  readonly code?: string;
  readonly correlationId?: string | null;
  readonly fieldErrors?: Readonly<Record<string, string>>;
}

export class ApiError extends Error {
  readonly status: number;
  /** Stable machine code such as `auth.invalid_credentials`. Empty when the API sent none. */
  readonly code: string;
  readonly correlationId: string | null;
  /** First message per field, keyed by the field name the API used. */
  readonly fieldErrors: Readonly<Record<string, string>>;

  constructor(init: ApiErrorInit) {
    super(messageFor(init.code ?? '', init.status));
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code ?? '';
    this.correlationId = init.correlationId ?? null;
    this.fieldErrors = init.fieldErrors ?? {};
  }

  /** Plain-English text safe to show a person. Never the server's own wording. */
  get userMessage(): string {
    return this.message;
  }
}

/** Exported so a spec can check it against the codes the API contract declares. */
export const CODE_MESSAGE_KEYS: Readonly<Record<string, MessageKey>> = {
  'validation.failed': 'error.validation',
  'resource.not_found': 'error.notFound',
  'resource.conflict': 'error.conflict',
  'idempotency.in_progress': 'error.conflict',
  'idempotency.mismatch': 'error.validation',
  'auth.unauthenticated': 'error.sessionEnded',
  'auth.session_revoked': 'error.sessionEnded',
  'auth.forbidden': 'error.forbidden',
  'auth.invalid_credentials': 'error.invalidCredentials',
  'auth.locked': 'error.locked',
  'auth.password_weak': 'error.validation',
  'auth.reset_token_invalid': 'error.malformed',
  'concurrency.stale': 'error.stale',
  'concurrency.required': 'error.stale',
  'rate.limited': 'error.rateLimited',
  'request.too_large': 'error.tooLarge',
  'request.malformed': 'error.malformed',
  'server.error': 'error.server',
  'server.unavailable': 'error.unavailable',
  'tenant.suspended': 'error.companySuspended',
  'csrf.invalid': 'error.csrf',
};

/** Message for a code, falling back to the HTTP status class when the code is unknown. */
export function messageFor(code: string, status: number): string {
  const byCode = CODE_MESSAGE_KEYS[code];
  if (byCode) return t(byCode);
  if (status === 0) return t('error.network');
  if (status === 401) return t('error.sessionEnded');
  if (status === 403) return t('error.forbidden');
  if (status === 404) return t('error.notFound');
  if (status === 429) return t('error.rateLimited');
  if (status === 503) return t('error.unavailable');
  if (status >= 500) return t('error.server');
  if (status === 422) return t('error.validation');
  return t('error.generic');
}

interface ProblemBody {
  code?: unknown;
  correlationId?: unknown;
  errors?: unknown;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function fieldErrorsOf(errors: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(errors)) return out;
  for (const entry of errors) {
    if (typeof entry !== 'object' || entry === null) continue;
    const field = asString((entry as Record<string, unknown>)['field']);
    const message = asString((entry as Record<string, unknown>)['message']);
    if (field && message && !(field in out)) out[field] = message;
  }
  return out;
}

/** Turns anything an HTTP call can throw into an `ApiError`. An existing one passes through. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof HttpErrorResponse) {
    const body: ProblemBody =
      typeof error.error === 'object' && error.error !== null ? (error.error as ProblemBody) : {};
    return new ApiError({
      status: error.status,
      code: asString(body.code),
      correlationId: asString(body.correlationId) ?? error.headers?.get('X-Correlation-Id') ?? null,
      fieldErrors: fieldErrorsOf(body.errors),
    });
  }
  return new ApiError({ status: 0 });
}
